import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@web/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@web/components/ui/card";
import { Input } from "@web/components/ui/input";
import { Label } from "@web/components/ui/label";
import { Textarea } from "@web/components/ui/textarea";
import { contactsApi } from "@web/lib/contacts-api";
import { useLanguage } from "@web/lib/contexts/language-context";
import { Mail, Send } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { type ContactFormData, contactSchema } from "./shared/contact-schema";

export function ContatoPage() {
	const { t } = useLanguage();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<ContactFormData>({
		resolver: zodResolver(contactSchema),
	});

	async function onSubmit(data: ContactFormData) {
		try {
			await contactsApi.post("/validador-contacts", data);
			toast.success("Mensagem enviada com sucesso!");
		} catch (error) {
			console.log(error);
			toast.error("Erro ao enviar mensagem");
		}
		reset();
	}

	return (
		<div className="w-full flex flex-1 items-center justify-center">
			<main className="flex flex-1 flex-col items-center p-4 md:p-8">
				<Card className="w-full max-w-3xl">
					<CardHeader>
						<div className="flex items-center gap-3">
							<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
								<Mail className="h-5 w-5 text-primary" />
							</div>
							<div>
								<CardTitle className="text-xl">{t.contactPage.title}</CardTitle>
								<CardDescription className="mt-1">
									{t.contactPage.subtitle}
								</CardDescription>
							</div>
						</div>
					</CardHeader>

					<CardContent className="pt-6">
						<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
							<div className="space-y-2">
								<Label htmlFor="email">{t.contactPage.emailInputLabel}</Label>
								<Input
									id="email"
									type="email"
									placeholder="email@email.com"
									{...register("email")}
								/>
								{errors.email && (
									<p className="text-sm text-destructive">
										{errors.email.message}
									</p>
								)}
							</div>

							<div className="space-y-2">
								<Label htmlFor="phone">{t.contactPage.phoneInputLabel}</Label>
								<Input
									id="phone"
									type="tel"
									placeholder="(00) 00000-0000"
									{...register("phone")}
								/>
								{errors.phone && (
									<p className="text-sm text-destructive">
										{errors.phone.message}
									</p>
								)}
							</div>

							<div className="space-y-2">
								<Label htmlFor="message">
									{t.contactPage.messageInputLabel}
								</Label>
								<Textarea
									id="message"
									placeholder={t.contactPage.messageInputPlaceholder}
									className="min-h-32 resize-none"
									{...register("message")}
								/>
								{errors.message && (
									<p className="text-sm text-destructive">
										{errors.message.message}
									</p>
								)}
							</div>

							<div className="flex flex-col lg:flex-row  justify-between gap-2 pt-4 border-t">
								<Button
									type="submit"
									className="min-w-40"
									disabled={isSubmitting}
								>
									<Send className="h-4 w-4" />
									{t.contactPage.submitButtonText}
								</Button>
								<a
									href="https://wa.me/5511914849799"
									target="_blank"
									rel="noopener noreferrer"
									className="flex flex-row items-center max-w-52 bg-green-500 text-white rounded-md p-1 shadow-lg hover:bg-green-600 transition-colors"
								>
									<img
										width={30}
										height={30}
										src="/whatsapp-icon.png"
										alt="WhatsApp"
									/>
									<p className="text-sm font-medium">
										{t.contactPage.whatsAppButton}
									</p>
								</a>
							</div>
						</form>
					</CardContent>
				</Card>
			</main>
		</div>
	);
}
